import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import LanguageSwitcher from './LanguageSwitcher';
import { getCompetitionCopy } from '../i18n/competition';
import { getLandingLabels } from '../i18n/landing';
import { getOemLabel } from '../i18n/oem';
import { getDopamineCopy } from '../i18n/dopamine';

const Navbar = () => {
  const { t, lang } = useLang();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  const landingLabels = getLandingLabels(lang);
  const serviceItems = [
    { to: '/services', label: t('nav.services') },
    { to: '/private-label-cosmetics', label: landingLabels.pl },
    { to: '/contract-cosmetics-manufacturing', label: landingLabels.cm },
    { to: '/influencer-beauty-brand', label: landingLabels.ib },
    { to: '/oem-skincare-manufacturer', label: getOemLabel(lang) },
  ];

  const links = [
    { to: '/', label: t('nav.home') },
    { to: '/dopamine', label: 'DOPAMINE', hot: true, badge: getDopamineCopy(lang).soon },
    { to: '/shop', label: t('nav.shop') },
    { to: '/services', label: t('nav.services'), dropdown: serviceItems },
    { to: '/products', label: t('nav.products') },
    { to: '/rnd', label: t('nav.rnd') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/certifications', label: t('nav.certificates') },
    { to: '/faq', label: t('nav.faq') },
    { to: '/competition', label: getCompetitionCopy(lang).navLabel, hot: true, badge: getCompetitionCopy(lang).free },
  ];

  const DARK_HERO_PATHS = ['/competition', '/contract-cosmetics-manufacturing', '/influencer-beauty-brand', '/dopamine'];
  const darkHero = DARK_HERO_PATHS.includes(location.pathname) && !scrolled && !mobileOpen;
  const baseLink = darkHero ? 'text-ivory/90 hover:text-gold-light' : 'text-espresso hover:text-gold';

  return (
    <header className={`fixed top-10 left-0 right-0 z-40 transition-all duration-500 ${scrolled ? 'bg-ivory/95 backdrop-blur-md shadow-[0_1px_20px_rgba(43,36,32,0.07)] py-3' : 'bg-transparent py-5'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center group shrink-0" data-testid="navbar-logo">
          <img src="/logo.png" alt="ARGE Cosmetics" className="h-14 md:h-16 w-auto transition-transform duration-500 group-hover:scale-105" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {links.map((l) => l.dropdown ? (
            <div key={l.to} className="relative group">
              <Link
                to={l.to}
                data-testid="nav-link-services"
                className={`relative inline-flex items-center gap-1 text-[0.8rem] tracking-[0.12em] uppercase whitespace-nowrap transition-colors duration-300 py-1 ${location.pathname === l.to || l.dropdown.some((d) => d.to === location.pathname) ? 'text-gold' : baseLink}`}
              >
                {l.label}
                <svg className="w-3 h-3 mt-0.5 transition-transform group-hover:rotate-180" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" /></svg>
              </Link>
              <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-ivory border border-gold-soft shadow-xl py-2 min-w-[240px]" data-testid="nav-services-dropdown">
                  {l.dropdown.map((d) => (
                    <Link key={d.to} to={d.to} data-testid={`nav-dropdown-${d.to.replace(/\//g, '')}`} className={`block px-5 py-2.5 text-[0.75rem] tracking-[0.1em] uppercase transition-colors ${location.pathname === d.to ? 'text-gold' : 'text-espresso hover:text-gold hover:bg-cream'}`}>{d.label}</Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace(/\//g, '') || 'home'}`}
              className={`relative text-[0.8rem] tracking-[0.12em] uppercase whitespace-nowrap transition-colors duration-300 py-1 group ${l.hot ? (darkHero ? 'text-gold-light font-medium' : 'text-gold font-medium') : location.pathname === l.to ? 'text-gold' : baseLink}`}
            >
              {l.label}{l.hot && <span className="ml-1.5 align-super text-[0.55rem] tracking-normal bg-gold text-espresso px-1 py-0.5 font-medium">{l.badge}</span>}
              <span className={`absolute left-0 -bottom-0.5 h-px bg-gold transition-all duration-300 ${location.pathname === l.to ? 'w-full' : 'w-0 group-hover:w-full'}`} />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <LanguageSwitcher light={darkHero} />
          <button onClick={() => setOpen(true)} aria-label="Open bag" className={`relative p-2 transition-colors ${darkHero ? 'text-ivory hover:text-gold-light' : 'text-espresso hover:text-gold'}`}>
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 rtl:-right-auto rtl:-left-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-gold text-espresso text-[0.6rem] font-medium rounded-full">{count}</span>
            )}
          </button>
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center whitespace-nowrap gold-sheen px-5 py-2.5 bg-espresso text-ivory text-[0.72rem] tracking-[0.18em] uppercase hover:bg-gold transition-colors duration-400"
          >
            {t('nav.quote')}
          </Link>
          <button className={`lg:hidden p-2 ${darkHero ? 'text-ivory' : 'text-espresso'}`} onClick={() => setMobileOpen((o) => !o)} aria-label="Menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-ivory border-t border-gold-soft mt-3 animate-fade-up">
          <nav className="flex flex-col px-6 py-4">
            {links.map((l) => l.dropdown ? (
              <div key={l.to} className="border-b border-cream">
                <span className="block py-3 text-sm tracking-[0.12em] uppercase text-mocha">{l.label}</span>
                {l.dropdown.map((d) => (
                  <Link key={d.to} to={d.to} className="block py-2.5 pl-4 text-sm tracking-[0.1em] uppercase text-espresso">{d.label}</Link>
                ))}
              </div>
            ) : (
              <Link key={l.to} to={l.to} className={`py-3 text-sm tracking-[0.12em] uppercase border-b border-cream last:border-0 ${l.hot ? 'text-gold font-medium' : 'text-espresso'}`}>{l.label}{l.hot && <span className="ml-1.5 text-[0.55rem] tracking-normal bg-gold text-espresso px-1 py-0.5 font-medium align-super">{l.badge}</span>}</Link>
            ))}
            <Link to="/contact" className="mt-4 text-center px-6 py-3 bg-espresso text-ivory text-xs tracking-[0.18em] uppercase">{t('nav.quote')}</Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
